<template>
  <div class="categories-component-container">
    <page-header :breadcrumbs="breadcrumbs">
      <template v-slot:topbar>
        <h1>{{ title }}</h1>
      </template>
    </page-header>
    <div class="list-tabs">
      <filter-tab
        v-disableable="{disabled: $store.getters['session/onTour']}"
        :status="unansweredFlashcardStatus"
        :count="flashcardCounts[currentScope]['unanswered']"
      />
      <tour-tip
        ref="tourTipFilterRef"
        :auto-hide="false"
        :tourtip-name="'filters'"
      >
        <filter-tab
          :status="redFlashcardStatus"
          :count="flashcardCounts[currentScope]['red']"
          @click.native="finishTour"
        />
      </tour-tip>
      <filter-tab
        v-disableable="{disabled: $store.getters['session/onTour']}"
        :status="yellowFlashcardStatus"
        :count="flashcardCounts[currentScope]['yellow']"
      />
      <filter-tab
        v-disableable="{disabled: $store.getters['session/onTour']}"
        :status="greenFlashcardStatus"
        :count="flashcardCounts[currentScope]['green']"
      />
      <filter-tab
        v-disableable="{disabled: $store.getters['session/onTour']}"
        :status="bookmarkedFlashcardStatus"
        :count="flashcardCounts[currentScope]['bookmarked']"
      />
    </div>
    <div
      v-disableable="{disabled: $store.getters['session/onTour']}"
      class="list-container tabbed"
    >
      <div class="list-heading row">
        <div class="counter-heading">
          Items
        </div>
        <div class="progress-heading">
          Progress
        </div>
        <div class="name-heading">
          Category
        </div>
      </div>
      <list-row
        :key="`${resourceCategories.length}-all`"
        class="all-categories-list-row"
        @row-clicked="handleAllRowClick"
      >
        <counter :count="categoryCounter(flashcardCounts[currentScope])" />
        <div class="center-container">
          <tagged-progress-bar
            :green="flashcardCounts[currentScope]['green']"
            :yellow="flashcardCounts[currentScope]['yellow']"
            :red="flashcardCounts[currentScope]['red']"
            :bookmarked="flashcardCounts[currentScope]['bookmarked']"
            :answered="flashcardCounts[currentScope]['answered']"
            :total="flashcardCounts[currentScope]['count']"
            :filter="flashcardStatusFilter.name"
          />
          <div class="name">
            <lock-button
              v-if="parentCategory.id && !$access($store, 'category', parentCategory.id)"
              :resource="{ type: 'category', id: parentCategory.id }"
              :source="{ type: 'Locked Category', value: parentCategory.id }"
            />
            <purchase-order-preview
              v-if="user.previewer"
              :resource="parentCategory"
            />
            <span :class="{ 'label-disabled': categoryCounter(flashcardCounts[currentScope]) <= 0 }">
              {{ allName }}
            </span>
          </div>
        </div>
        <icon
          name="arrow"
          class="arrow"
          variant="4fb9de"
        />
      </list-row>
      <template v-for="category in scopedCategories">
        <list-row
          :key="category.id"
          class="category-list-row"
          @row-clicked="handleCategoryRowClick(category)"
        >
          <counter :count="categoryCounter(flashcardCounts[category.id])" />
          <div class="center-container">
            <tagged-progress-bar
              :green="flashcardCounts[category.id]['green']"
              :yellow="flashcardCounts[category.id]['yellow']"
              :red="flashcardCounts[category.id]['red']"
              :bookmarked="flashcardCounts[category.id]['bookmarked']"
              :answered="flashcardCounts[category.id]['answered']"
              :total="flashcardCounts[category.id]['count']"
              :filter="flashcardStatusFilter.name"
            />
            <div class="name">
              <lock-button
                v-if="!$access($store, 'category', category.id)"
                :resource="{ type: 'category', id: category.id }"
                :source="{ type: 'Locked Category', value: category.id }"
              />
              <purchase-order-preview
                v-if="user.previewer"
                :resource="category"
              />
              {{ category.name }}
            </div>
          </div>
          <icon
            name="arrow"
            class="arrow"
            variant="4fb9de"
          />
        </list-row>
      </template>
    </div>
  </div>
</template>

<script>
import AuthenticationMixin from '@/mixins/authentication';
import CategoriesMixin from '@/mixins/categories';
import QuestionsUiV2Mixin from '@/mixins/questions-ui-v2';
import Counter from '@components/singletons/counter';
import FilterTab from '@components/categories/filter-tab';
import GlobalMixin from '@/mixins/global';
import Icon from '@components/singletons/icon';
import LockButton from '@components/singletons/lock-button';
import ListRow from '@components/ui/listRow';
import PageHeader from '@components/singletons/pageHeader';
import TaggedProgressBar from '@components/progress/TaggedProgressBar';
import TourTip from '@components/singletons/tour-tip';
import PurchaseOrderPreview from '@components/singletons/purchaseOrderPreview';

import { capitalize } from '@/utils';
import { setChecklistData, COMPLETE_TOOLTIPS_TOUR_KEY } from '@/utils/checklist';

export default {
  components: { Counter, FilterTab, Icon, ListRow, LockButton, PageHeader, TourTip, PurchaseOrderPreview, TaggedProgressBar },
  mixins: [AuthenticationMixin, CategoriesMixin, GlobalMixin, QuestionsUiV2Mixin],
  computed: {
    allName() {
      if (this.categoryTypeId) {
        return `All ${this.categoryType.displayNamePlural}`;
      } else if (this.parentCategory.name) {
        return `All ${this.parentCategory.name}`;
      } else {
        const data = this.flashcardCounts['all'];
        let singular = false;
        if (this.flashcardStatusFilter.id && this.flashcardStatusFilter != this.unansweredFlashcardStatus) {
          singular = data[this.flashcardStatusFilter.name] === 1;
        } else {
          singular = data['answered'] === data['count'] - 1;
        }
        let word = singular ? 'Item' : 'Items';
        if (this.currentResource.resourceId && this.$store.getters['categoryTypes/categoryTypes'].find(ct => ct.id === this.currentResource.resourceId)) {
          let ct = this.$store.getters['categoryTypes/categoryTypes'].find(ct => ct.id === this.currentResource.resourceId);
          word = singular ? ct.displayNameSingular : ct.displayNamePlural;
        }
        return `Study All ${capitalize(word)}`;
      }
    },
    allRoute() {
      let category = this.parentCategory.id ? this.parentCategory : {id: 'all'};
      if(!this.categoryFinished(category)) {
         return { name: 'categories#flashcard', params: { categoryId: category.id }, query: this.currentQuery };
      } else {
        return { query: this.currentQuery};
      }
    },
    breadcrumbs() {
      if (!this.parentCategory.id) return [];
      let categories = this.parentCategory.path.split('.').map(id => this.categories[id]);
      let crumbs = categories.map(c => {
        return { name: c.name, route: {name: 'categories#index', params: { categoryId: c.id}, query: this.currentQuery}};
      });
      return crumbs;
    },
    bookmarkedFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/bookmarkedFlashcardStatus'];
    },
    categoryTypeId() {
      return this.$route.params['categoryTypeId'];
    },
    categoryType() {
      const categoryTypeId = this.$route.params['categoryTypeId'];
      if (categoryTypeId) {
        return this.$store.getters['categoryTypes/all'][categoryTypeId];
      }
      return null;
    },
    currentScope() {
      return this.parentCategory.id || 'all';
    },
    flashcardStatusFilter() {
      return this.$store.getters['flashcardStatuses/flashcardStatusFilter'];
    },
    greenFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/greenFlashcardStatus'];
    },
    isParentCategorySimulation() {
      const categoryTypeId = this.parentCategory.categoryTypeId;
      const simulationTypeIds = this.$store.getters['categoryTypes/testSimulationCategoryTypes'].map(t => t.id);
      return simulationTypeIds.includes(categoryTypeId);
    },
    parentCategory() {
      return this.categories[this.$route.params.categoryId] || {};
    },
    redFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/redFlashcardStatus'];
    },
    title() {
      if (this.parentCategory.name) {
        return this.parentCategory.name;
      } else {
        return this.currentResource.name;
      }
    },
    unansweredFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/unansweredFlashcardStatus'];
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
    yellowFlashcardStatus() {
      return this.$store.getters['flashcardStatuses/yellowFlashcardStatus'];
    },
  },
  watch: {
    '$route.params.categoryId': {
      immediate: true,
      handler: function(id) {
        if (this.isParentCategorySimulation) {
          this.$router.push({
            name: 'sims#show',
            params: {
              simId: this.parentCategory.id,
            },
          });
        }
        if (this.parentCategory.id) {
          this.$analytics.events.viewedCategory(this.$store, this.parentCategory);
        }
      },
    },
    '$route': {
      immediate: true,
      handler: function(to, from) {
        // if a user switches directly between flashcard nav items, reset filter
        if (to && from && to.query.nav !== from.query.nav) {
          this.$store.commit('flashcardStatuses/setFlashcardStatusFilter');
        }
      },
    },
  },
  mounted() {
    this.$store.commit('flashcardStatuses/setFlashcardStatusFilter');
    this.$store.dispatch('flashcardStates/fetchStates', ['bookmarks', 'flashcards']);
  },
  methods: {
    categoryCounter(data) {
      if (this.flashcardStatusFilter.id && this.flashcardStatusFilter != this.unansweredFlashcardStatus) {
        return data[this.flashcardStatusFilter.name];
      }
      return data['unanswered'];
    },
    categoryFinished(category) {
      const data = this.flashcardCounts[category.id];

      if(this.flashcardStatusFilter.id && this.flashcardStatusFilter != this.unansweredFlashcardStatus) {
        return data[this.flashcardStatusFilter.name] < 1;
      } else {
        return data['answered'] >= data['count'];
      }
    },
    categoryRoute(category) {
      if (category.children.length > 0) {
        return { name: 'categories#index', params: { categoryId: category.id }, query: this.currentQuery };
      } else {
        if (this.$access(this.$store, 'category', category.id) && !this.categoryFinished(category)) {
          if (this.questionUiV2Enabled) {
            return { name: 'categories#flashcard-v2', params: { categoryId: category.id }, query: this.currentQuery };
          }
          return { name: 'categories#flashcard', params: { categoryId: category.id }, query: this.currentQuery };
        } else {
          return { query: this.currentQuery};
        }
      }
    },
    finishTour() {
      if (this.$store.getters['session/onTour']) {
        this.$refs.tourTipFilterRef.open = false;
        const self = this;
        setTimeout(function() {
          self.$store.commit('session/setOnTour', false);
          self.$store.commit('session/setTourType', '');
          setChecklistData(self.$store, COMPLETE_TOOLTIPS_TOUR_KEY, self.$notify);
          self.$modal.show('questions-finish-tour-modal');
        }, 1500);
      }
    },
    handleAllCategoryType() {
      const categories = this.$store.getters['categories/all']
        .filter(c => c.categoryTypeId === Number(this.categoryTypeId))
        .filter(c => this.$access(this.$store, 'category', c.id))
        .filter(c => !c.children.length)
        .filter(c => this.flashcardCounts[c.id][this.flashcardStatusFilter?.name] > 0)
        .sort((a, b) =>
          Math.random() * this.flashcardCounts[b.id][this.flashcardStatusFilter?.name] -
          Math.random() * this.flashcardCounts[a.id][this.flashcardStatusFilter?.name]
        );

      if (categories.length > 0) {
        const route = this.categoryRoute(categories[0]);
        this.$router.push(route);
      }
    },
    handleAllRowClick() {
      if (this.categoryTypeId) {
        this.handleAllCategoryType();
        return;
      }
      const locked = this.parentCategory.id && !this.$access(this.$store, 'category', this.parentCategory.id);
      if (locked) {
        this.showUpsell(this.parentCategory);
      } else {
        this.$router.push(this.allRoute);
      }
    },
    handleCategoryRowClick(category) {
      const locked = !this.$access(this.$store, 'category', category.id) && category.children.length < 1;
      if (locked) {
        this.showUpsell(category);
      } else {
        const route = this.categoryRoute(category);
        this.$router.push(route);
      }
    },
    showUpsell(category) {
      if (!this.$access(this.$store, 'category', category.id) && category.children.length < 1) {
        this.$store.dispatch('ui/toggleUpsellModal', {
          show: true,
          resource: { type: 'category', id: category.id },
          source: { type: 'Locked Category', value: category.id },
        });
      }
    },
  },
};
</script>

<style lang='scss' scoped>
  $heading-color: $grey-2 !default;

  .categories-component-container {
    background: #EDF1F5;
    @include full-padding;

    .heading {
      color: $heading-color;
      display: flex;
      font-size: rem-calc(18);
      font-weight: $semi-bold;
      margin-bottom: rem-calc(28);

      .back {
        align-items: center;
        color: #354052;
        display: flex;
        font-size: rem-calc(12);
      }
    }

    .list-tabs {
      align-items: flex-end;
      display: flex;
      position: relative;
      z-index: 9;
    }

    .list-heading {
      .name-heading {
        margin-left: rem-calc(16);
      }
    }
  }

  .label-disabled {
    color: $grey-3;
  }
</style>
